import React, { useCallback, useMemo, useState } from 'react';
import {
  CHIP_SIZE,
  CHIP_TYPE,
  COMMENT_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  priorityList,
  TASK_PRIORITY
} from '../../../global/constants';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../../services/taskServices';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../global/analyticsConstants';
import Dropdown from '../../dropdown/mainDropdown';
import DropdownItem from '../../dropdown/dropdownItem';
import { ProjectModel } from '../../../mobx/models/project';
import { TaskModel } from '../../../mobx/models/task';
import { rootStore } from '../../../mobx/rootStore';
import { nanoid } from 'nanoid';
import { getRawComment } from '../../../helpers/common';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import { TaskTemplateModel } from '../../../mobx/stores/projectTemplateStore';
import IconButton from '../../button/iconButton';
import Chip from '../../chipComponent';

// Props interface for priority dropdown component
interface Props {
  taskData?: TaskModel;
  currentProject?: ProjectModel | ProjectTemplateModel;
  isTaskCheckBox?: boolean;
  isCreateTask?: boolean;
  isRecurringTask?: boolean;
  isTemplateProject?: boolean;
  isTaskTemplate?: boolean;
  currentTaskTemplate?: TaskTemplateModel;
  isIconButton?: boolean;
}

const PriorityDropdown: React.FC<Props> = (props) => {
  //Props declaration
  const {
    taskData,
    isTaskCheckBox,
    isCreateTask,
    currentProject,
    isRecurringTask,
    isTemplateProject,
    isTaskTemplate,
    currentTaskTemplate,
    isIconButton
  } = props;
  //Mobx store variables
  const { taskStore, currentUser, projectTemplateStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { currentProjectTemplate } = projectTemplateStore;
  // State variables
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Get priority based on task state
  let priorityData = isCreateTask
    ? isTaskTemplate
      ? currentTaskTemplate?.priority
      : createTaskInput?.priority
    : taskData?.priority;

  // Set default priority if none exists
  if (!priorityData && priorityData !== 0) {
    priorityData =
      priorityList?.find((item) => item.key === currentProject?.priority?.default)?.key || TASK_PRIORITY.NO_PRIORITY;
  }

  /**
   * @desc Gets priority details by key
   * @param key - Priority key to lookup
   */
  const getPriorityData = useCallback((key: number) => {
    return priorityList.find((item) => item.key === key);
  }, []);

  /**
   * @desc Updates task priority and handles related state changes
   * @param key - New priority value to set
   */
  const updateTaskPriority = useCallback(
    async (key: number) => {
      if (!currentUser) return;
      // Return if priority is not changed
      if (Number(key) === taskData?.priority) {
        return;
      }
      if (isRecurringTask) {
        taskData?.setPriority(key);
        //Update priority for recurring task
        const payload = { priority: Number(key), projectId: taskData?.projectId };
        const response = await updateRecurringTaskDetails(taskData?.id, payload);
        if (response) {
          trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED);
        }
      } else if (isCreateTask) {
        //Update priority for create task input
        if (isTaskTemplate) {
          currentTaskTemplate?.setPriority(key);
        } else {
          createTaskInput.setPriority(key);
        }
      } else {
        //Update priority for task

        const newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.CHANGE_PRIORITY,
          previousPriority: taskData?.priority || TASK_PRIORITY.NO_PRIORITY,
          priority: Number(key)
        };
        const updatedComment = getRawComment(newComment, currentUser);
        taskData?.setPriority(key);

        if (!isTemplateProject) {
          // Send API request to update task details and add a comment
          taskData?.upsertComment(updatedComment);
          const payloadTask = { priority: Number(key), projectId: taskData?.projectId };
          const result = await updateTaskDetails(taskData?.id, payloadTask);
          const commentResult = await createNewComment(taskData?.id, {
            Type: COMMENT_TYPE.CHANGE_PRIORITY,
            PreviousPriority: newComment?.previousPriority,
            Priority: Number(key)
          });
          if (result && commentResult) {
            trackAnalyticActivity(TASK_ANALYTICS.PRIORITY_CHANGED); // Track priority change and update UI
          }
        } else {
          const updatedTaskList = currentProjectTemplate?.tasks.map((item) => {
            if (item?.id === taskData?.id) {
              item?.setPriority(key);
            }
            return item;
          });
          rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(updatedTaskList || []);
        }
      }
    },
    [
      createTaskInput,
      currentTaskTemplate,
      currentProjectTemplate?.tasks,
      currentUser,
      isCreateTask,
      isRecurringTask,
      isTaskTemplate,
      isTemplateProject,
      taskData
    ]
  );

  // Renders priority dropdown items list
  const renderPriorityData = useMemo(() => {
    return priorityList.map((priorityData) => (
      <DropdownItem
        label={priorityData.name}
        key={priorityData.key}
        iconName={priorityData.iconName}
        onClick={() => updateTaskPriority(priorityData.key)}
        isSelected={taskData?.priority === priorityData.key} // Check if the priority is selected
      />
    ));
  }, [updateTaskPriority, taskData?.priority]); // Add `props.taskData?.priority` as a dependency

  return (
    <Dropdown
      content={
        isIconButton ? (
          <IconButton
            iconName={getPriorityData(priorityData || TASK_PRIORITY.NO_PRIORITY)?.iconName || ''}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            iconViewBox='0 0 16 16'
            size={isTaskCheckBox ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.MEDIUM}
            isMultiColor={true}
            className={isDropdownOpen ? 'active-status-dropdown' : ''}
            type={!isTaskCheckBox ? 'square' : 'rounded'}
          />
        ) : (
          <Chip
            size={isCreateTask ? CHIP_SIZE.SMALL : CHIP_SIZE.MEDIUM}
            variant={isCreateTask ? CHIP_TYPE.FILL : CHIP_TYPE.EMPTY}
            title={getPriorityData(priorityData!)?.name}
            leadingIconName={getPriorityData(priorityData!)?.iconName}
            leadingIconViewBox='0 0 16 16'
            isMulticolorIcon={true}
            isActive={isDropdownOpen}
          />
        )
      }
      trigger='click'
      modalTitle='Priority'
      updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
      {renderPriorityData}
    </Dropdown>
  );
};

export default PriorityDropdown;
